import {useContext, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Divider, Grid, IconButton, Typography} from '@mui/material';

import SocialIconButtons from './SocialIconButtons';
import {InfoIcon} from '../../../../../assets/employee_satisfaction_bar';
import {EmpionTooltip} from '../../../../../components/EmpionTooltip/EmpionTooltip';
import {PendingTalentSurveyModal} from '../../../../../components/PendingTalentSurveyModal/PendingTalentSurveyModal';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {useUserData} from '../../../../../contexts/UserContext/useUserData';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import {ApplicantRecruiting} from '../../../../../types/shared-recruiting-types';
import {CompanyAdminRoleData} from '../../../../../types/User';
import {renderSalaryRange} from '../../../../../utils/applicantSalaryRange/renderSalaryRange';
import {formatSimpleDuration} from '../../../../../utils/dateUtils/calculateDuration';
import {transformYearMonthDayToDateMonthYear} from '../../../../../utils/dateUtils/dateFormattingUtils';
interface Props {
  applicant: ApplicantRecruiting;
  jobTitle?: string;
}

export default function CardView({applicant, jobTitle}: Props) {
  const {t} = useTranslation();
  const {t: tr} = useTranslation('translation', {keyPrefix: 'companyApplicants.applicantsCard.cardDetails'});
  const [isEmailIconClicked, setEmailIconClicked] = useState(false);
  const [isSalaryOverflowing, setIsSalaryOverflowing] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const currentLanguage = useContext(LanguageContext);
  const salaryRef = useRef<HTMLDivElement>(null);
  const {currentUser} = useUserData();

  useEffect(() => {
    const element = salaryRef.current;
    if (element) {
      setIsSalaryOverflowing(element.offsetWidth < element.scrollWidth);
    }
  }, []);

  const professionalExperience = formatSimpleDuration(
    {
      amount: applicant?.keyFacts?.professionalExperience?.duration || 0,
      unit: (applicant?.keyFacts?.professionalExperience?.unit || 'years') as 'years' | 'months',
    },
    currentLanguage
  );

  const isValidDate = (dateString: string) => {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  };

  const getStartDateDisplay = () => {
    if (applicant?.keyFacts?.startDate && isValidDate(applicant.keyFacts.startDate)) {
      return transformYearMonthDayToDateMonthYear(applicant.keyFacts.startDate);
    } else {
      return tr('notSpecified');
    }
  };

  const handleInfoModalOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInfoModalOpen(true);
  };

  return (
    <>
      <Grid container justifyContent={'space-between'}>
        <Grid container alignItems='center'>
          <Typography paddingBottom={1} width='100%' variant='body1' color={greenColors[70]}>
            {jobTitle}
          </Typography>
          <Grid item display='flex' flexDirection='column' xs={8}>
            <Typography variant='h4'>{applicant.personalInformation?.firstName}</Typography>
            <Typography
              variant='h4'
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {applicant.personalInformation?.lastName}
            </Typography>
          </Grid>

          <Grid item container xs={4} justifyContent={'end'}>
            <SocialIconButtons
              applicantEmail={applicant.personalInformation.email}
              applicantLinkedinUrl={applicant.personalInformation.linkedinUrl}
              isEmailIconClicked={isEmailIconClicked}
              setEmailIconClicked={setEmailIconClicked}
            />
          </Grid>
        </Grid>

        <Grid item mt={2}>
          <Typography variant='body1'>{applicant.personalInformation?.mobilePhoneNumber}</Typography>
        </Grid>
      </Grid>

      <Divider sx={{my: 2, backgroundColor: greenColors[600]}} />

      <Typography variant='h4'>{tr('keyFacts')}</Typography>
      <Grid container>
        <Grid container item xs={6} flexDirection={'column'}>
          <Grid item>
            <Typography variant='body1'>{tr('experience')}:</Typography>
          </Grid>
          <Grid item>
            <Typography variant='body1'>{tr('salary')}:</Typography>
          </Grid>
          <Grid item>
            <Typography variant='body1'>{tr('startDate')}:</Typography>
          </Grid>
          <Grid item>
            <Typography variant='body1'>{t('shared.cultureType')}:</Typography>
          </Grid>
        </Grid>
        <Grid container item xs={6} flexDirection={'column'}>
          <Grid item>
            <Typography variant='body1'>{professionalExperience}</Typography>
          </Grid>
          <Grid item sx={{maxWidth: '100%'}}>
            {isSalaryOverflowing ? (
              <EmpionTooltip
                title={applicant ? renderSalaryRange(applicant, tr) : tr('notSpecified')}
                isEnabled={isSalaryOverflowing}
              >
                <Typography
                  variant='body1'
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  ref={salaryRef}
                >
                  {applicant ? renderSalaryRange(applicant, tr) : tr('notSpecified')}
                </Typography>
              </EmpionTooltip>
            ) : (
              <Typography
                variant='body1'
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                ref={salaryRef}
              >
                {applicant ? renderSalaryRange(applicant, tr) : tr('notSpecified')}
              </Typography>
            )}
          </Grid>
          <Grid item>
            <Typography variant='body1'>{getStartDateDisplay()}</Typography>
          </Grid>
          <Grid item>
            {applicant.keyFacts?.cultureTypeName && applicant.keyFacts.cultureTypeName[currentLanguage] ? (
              <Typography variant='body1'>{applicant.keyFacts.cultureTypeName[currentLanguage]}</Typography>
            ) : (
              <>
                <Typography variant='body1'>
                  {tr('pendingCultureType')}{' '}
                  <IconButton onClick={handleInfoModalOpen}>
                    <InfoIcon fill={orangeColors[900]} height={23} width={23} />
                  </IconButton>
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
      <PendingTalentSurveyModal
        open={infoModalOpen}
        setOpen={setInfoModalOpen}
        companyId={(currentUser?.role.data as CompanyAdminRoleData)?.companyId || ''}
        applicantId={applicant.personalInformation.resourceId}
      />
    </>
  );
}
